<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import type { IPosition } from '../../etc/types';

  import { Container } from '../../services/container';
  import Toast from '../misc/Toast.svelte';
  import { Auth } from '../../services/auth';
  import { AppState } from '../../etc/stores';

  import Routes from './Router.svelte';

  import { isMobile } from '../../etc/utils';
  import { useMobileKeyboard } from '../../etc/hooks/useMobileKeyboard';
  import ModalInternalManager from '../misc/ModalInternalManager.svelte';
  import PoweredBy from '../misc/PoweredBy.svelte';
  import flags from '../../etc/flagKeys';

  const auth = Container.get(Auth);

  const { isOpen: isMobileAndroidKeyboardOpen } = useMobileKeyboard();

  let hideFooter = false;
  $: hideFooter =
    $isMobileAndroidKeyboardOpen ||
    $AppState.website.footerVisibility === false;

  let stateDisabledClickPopover = $AppState.website.disabledClickPopover;
  let disabledClickPopover = false;
  let customStyles = '';

  const isChangeAnimation =
    auth.provider === 'wix' &&
    isMobile() &&
    $AppState.featureFlags.includes(flags.MOBILE_WIX_RESIZE_WINDOW);

  export let direction: IPosition;

  let lastPopoverState = $AppState.website.popoverOpen;

  const positionSettings = $AppState.settings || {};

  let scrollY = window.scrollY;

  const disableScroll = () => {
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.right = '0px';
    body.style.left = '0px';
    body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    body.classList.add('twism-locked');
  };

  const enableScroll = () => {
    const { body } = document;
    const { top } = body.style;
    const yValue = -parseInt(top || '0', 10);
    body.style.position = '';
    body.style.top = '';
    body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.height = '';
    body.classList.remove('twism-locked');
    window.scrollTo(0, yValue);
  };

  $: {
    if (isMobile()) {
      const hasStateChanged =
        lastPopoverState !== $AppState.website.popoverOpen;
      const isBodyLocked = document.body.classList.contains('twism-locked');
      lastPopoverState = $AppState.website.popoverOpen;

      scrollY = window.scrollY;
      if (hasStateChanged) {
        if (isBodyLocked) {
          setTimeout(() => {
            enableScroll();
          }, 600);
        } else if ($AppState.website.popoverOpen) disableScroll();
      }
    }
  }

  const onLoadStyle = () => {
    const placement =
      positionSettings.position === 'bottomLeft' ? 'left' : 'right';
    const sideSpacing =
      (isMobile()
        ? positionSettings.mobileSideSpacing
        : positionSettings.desktopSideSpacing) ?? 30;
    const bottomSpacing =
      (isMobile()
        ? positionSettings.mobileBottomSpacing
        : positionSettings.desktopBottomSpacing + 64 + 16) ?? 30;

    customStyles = [
      `${placement}: ${sideSpacing}px`,
      `bottom: ${bottomSpacing}px`,
      isMobile()
        ? ''
        : `max-height: ${window.innerHeight - bottomSpacing - 24}px`
    ].join(';');
  };

  onMount(async () => {
    window.addEventListener('resize', onLoadStyle, true);
  });

  onDestroy(() => {
    window.removeEventListener('resize', onLoadStyle, true);
  });

  $: {
    onLoadStyle();
  }
</script>

{#if $AppState.website.popoverOpen}
  <div
    class={`twism-popover ${auth.provider} ${direction} ${
      $AppState.website.iconsBackground !== 'themed' ? 'twism-light' : ''
    }`}
    class:autoheight={$AppState.website.popOverAutoHeight}
    class:disabled-click={disabledClickPopover || stateDisabledClickPopover}
    style={customStyles}
    in:fly={{ y: 50 }}
    out:fly={{ y: isChangeAnimation ? 0 : 50 }}
    on:introstart={() => (disabledClickPopover = true)}
    on:outrostart={() => (disabledClickPopover = true)}
    on:introend={() => (disabledClickPopover = false)}
    on:outroend={() => (disabledClickPopover = false)}
  >
    <ModalInternalManager />
    <Toast />
    <div class="twism-popover__container">
      <div
        class="twism-popover__container-animation"
        in:fade={{
          delay: isChangeAnimation ? 700 : 0,
          duration: isChangeAnimation ? 400 : 0
        }}
        out:fade={{
          delay: isChangeAnimation ? 0 : 500,
          duration: isChangeAnimation ? 500 : 0
        }}
      >
        <div class="twism-popover__router" class:full-height={hideFooter}>
          <Routes />
        </div>
        {#if !hideFooter}
          <PoweredBy
            class="{$AppState.website.popoverOpen
              ? 'twism-footer-active'
              : ''} {$AppState.website.popoverFooterAlignment === 'right'
              ? 'twism-footer-right'
              : ''}"
          />
        {/if}
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .twism-popover {
    box-shadow: $shadow;
    width: 360px;
    height: 600px;
    border-radius: 23px;
    position: fixed;
    overflow: hidden;
    max-height: 90vh;
    transition: bottom 0.2s;

    @media screen and (max-height: 620px) {
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__container {
      box-sizing: border-box;
      height: 100%;
      min-height: 100%;
      border-radius: 24px;
      webkit-clip-path: inset(0 0 0 0 round 24px);
      clip-path: inset(0 0 0 0 round 24px);
      background-color: $color-white;

      &-animation {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        height: 100%;
        min-height: 100%;
      }
    }
    &__router {
      width: 100%;
      height: 100%;
      max-height: calc(100% - 35px);
      flex: 1;
      background-color: $color-white;

      &.full-height {
        max-height: unset;
      }
    }

    &.disabled-click {
      pointer-events: none;
    }

    &.autoheight {
      .twism-popover__container {
        height: auto;
        min-height: unset;
      }

      .twism-popover__router {
        max-height: unset;
        padding-bottom: 16px;
      }
    }

    &.bottomRight {
      right: 30px;
      bottom: 30px;
      left: auto;
    }

    &.bottomLeft {
      left: 30px;
      bottom: 30px;
      right: auto;
    }

    &.wix {
      z-index: calc(var(--above-all-z-index) - 3);
    }

    &.woocommerce,
    &.shopify,
    &.bigcommerce {
      z-index: 1000001;
      display: block !important;
    }
  }

  :global(.twism-popover__container .twism-popover__footer) {
    margin-bottom: 20px;
    height: 12px;

    @media screen and (max-width: 468px) {
      position: relative;
      bottom: -10px;
    }

    &.twism-footer-right {
      align-self: flex-end;
      margin-right: 24px;
      position: relative;
      bottom: 0;
    }
  }

  @media screen and (max-width: 468px) {
    .twism-popover {
      width: 100%;
      height: 100%;
      bottom: 0 !important;
      top: 0 !important;
      right: 0 !important;
      left: 0 !important;
      border-radius: 0;
      overflow-y: overlay;
      max-height: 100vh;

      &__container {
        webkit-clip-path: inset !important;
        clip-path: inherit !important;
        border-radius: 0 !important;
      }
    }
  }
</style>
